import React, { createContext, useState, useEffect, useCallback } from 'react';
import { getConversations, getMessagesByUserId, markConversationAsRead, sendMessage } from '../api/chat';
import useAuth from '../hooks/useAuth';
import useWebSocket from '../hooks/useWebSocket';

export const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
    const { user, isAuth } = useAuth();
    const { addEventListener, sendMessage: sendWebSocketMessage, socket } = useWebSocket();

    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [typingUsers, setTypingUsers] = useState({});

    // Define fetchConversations first (before any useEffect that uses it)
    const fetchConversations = useCallback(async () => {
        if (!isAuth) return;

        try {
            setLoading(true);
            const response = await getConversations();
            setConversations(response.data.data.conversations);

            // Calculate and sync unread count
            const total = response.data.data.conversations.reduce(
                (acc, convo) => acc + (convo.unreadCount || 0),
                0
            );
            setUnreadCount(total);
            
            // Emit real-time unread count update
            if (socket) {
                socket.emit('message:unread-count-sync', { 
                    totalCount: total,
                    timestamp: Date.now()
                });
            }
            
        } catch (error) {
            console.error('Error fetching conversations:', error);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    }, [isAuth, socket]);

    const fetchMessages = useCallback(
        async (userId, options = {}) => {
            if (!isAuth || !userId) return;

            try {
                setLoading(true);
                const response = await getMessagesByUserId(userId, options);

                setMessages(response.data.data.messages);
                setActiveConversation({
                    userId,
                    recipient: response.data.data.recipient,
                });

                await markConversationAsRead(userId);
                fetchConversations();

                return response.data.data;
            } catch (error) {
                console.error('Error fetching messages:', error);
                setError('Failed to load messages');
            } finally {
                setLoading(false);
            }
        },
        [isAuth, fetchConversations]
    );

    const sendNewMessage = useCallback(
        async (userId, messageData) => {
            if (!isAuth || !userId) return;

            try {
                const socketPayload = {
                    type: 'message:send',
                    recipientId: userId,
                    ...messageData
                };

                sendWebSocketMessage(socketPayload);
            } catch (error) {
                console.error('Error sending message via socket:', error);
                setError('Failed to send message');
                throw error;
            }
        },
        [isAuth, sendWebSocketMessage]
    );

    const sendNewMessageHttp = useCallback(
        async (userId, messageData, onUploadProgress) => {
            if (!isAuth || !userId) return;
            
            try {
                setLoading(true);
                const response = await sendMessage(userId, messageData, onUploadProgress);
                
                console.log("RD : ", response)
                const data = response.data.data.message;
                
                if (data.sender && data.sender._id) {
                    setMessages((prev) => {
                        const exists = prev.some(msg => msg._id === data._id);
                        if (exists) return prev;
                        return [...prev, data];
                    });
                    markConversationAsRead(data.sender._id);
                }

            } catch (error) {
                console.error('Error sending message:', error);
                setError('Failed to send message');
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [isAuth]
    );

    const checkMessageUpdates = useCallback(
        async () => {
            if (!isAuth) return;

            try {
                const socketPayload = {
                    type: 'message:updates'
                };

                sendWebSocketMessage(socketPayload);
            } catch (error) {
                console.error('Error sending message via socket:', error);
                setError('Failed to send message');
                throw error;
            }
        },
        [isAuth, sendWebSocketMessage]
    );

    const sendTypingStart = useCallback(
        (recipientId) => {
            if (!isAuth || !recipientId) return;

            sendWebSocketMessage({
                type: 'typing:start',
                recipientId,
            });
        },
        [isAuth, sendWebSocketMessage]
    );

    const sendTypingStop = useCallback(
        (recipientId) => {
            if (!isAuth || !recipientId) return;

            sendWebSocketMessage({
                type: 'typing:stop',
                recipientId,
            });
        },
        [isAuth, sendWebSocketMessage]
    );

    // Single consolidated useEffect for all socket event listeners
    useEffect(() => {
        if (!isAuth) return;

        console.log('[ChatContext] Setting up socket event listeners');

        const cleanupReceivedMessage = addEventListener('message:received', (data) => {
            console.log("Message received:", data);
            if (data && data.sender && data.sender._id) {
                const isActiveConversation = activeConversation?.recipient._id === data.sender._id;
                
                if (isActiveConversation) {
                    setMessages((prev) => {
                        const exists = prev.some(msg => msg._id === data._id);
                        if (exists) return prev;
                        return [...prev, data];
                    });
                    
                    // Auto-mark as read if conversation is active
                    setTimeout(() => {
                        markConversationAsRead(data.sender._id);
                    }, 1000);
                } else {
                    // Emit unread count update for inactive conversations
                    if (socket) {
                        socket.emit('message:unread-count-increment', {
                            senderId: data.sender._id,
                            messageId: data._id
                        });
                    }
                }
                
                // Always refresh conversations for new messages
                setTimeout(() => fetchConversations(), 500);
            }
        });

        const cleanupSentMessage = addEventListener('message:sent', (data) => {
            console.log("Message sent successfully:", data);
            
            if (data && data._id) {
                setMessages((prev) => {
                    const exists = prev.some(msg => msg._id === data._id);
                    if (exists) return prev;
                    return [...prev, data];
                });
                
                // Refresh conversations to update last message
                setTimeout(() => fetchConversations(), 100);
            }
        });

        const cleanupTypingStart = addEventListener('typing:start', (data) => {
            console.log("Typing start:", data);
            if (data.userId) {
                setTypingUsers((prev) => ({
                    ...prev,
                    [data.userId]: true
                }));
            }
        });

        const cleanupTypingStop = addEventListener('typing:stop', (data) => {
            console.log("Typing stop:", data);
            if (data.userId) {
                setTypingUsers((prev) => {
                    const updated = { ...prev };
                    delete updated[data.userId];
                    return updated;
                });
            }
        });

        return () => {
            console.log('[ChatContext] Cleaning up socket event listeners');
            cleanupReceivedMessage();
            cleanupSentMessage();
            cleanupTypingStart();
            cleanupTypingStop();
        };
    }, [isAuth, activeConversation, addEventListener, fetchConversations, socket]);

    // Initial fetch and periodic updates
    useEffect(() => {
        if (isAuth) {
            fetchConversations();

            // Set up interval to fetch conversations every 30 seconds
            const interval = setInterval(() => {
                fetchConversations();
            }, 30000);

            // Reset typing indicators every 6 seconds
            const resetTypingInterval = setInterval(() => {
                setTypingUsers({});
            }, 6000);

            return () => {
                clearInterval(interval);
                clearInterval(resetTypingInterval);
            };
        }
    }, [isAuth, fetchConversations]);

    // Clear messages when no active conversation
    useEffect(() => {
        if (!activeConversation) {
            setMessages([]);
        }
    }, [activeConversation]);

    const chatContextValue = {
        conversations,
        activeConversation,
        messages,
        loading,
        error,
        unreadCount,
        typingUsers,
        fetchConversations,
        fetchMessages,
        sendMessage: sendNewMessage,
        sendNewMessageHttp,
        setActiveConversation,
        sendTypingStart,
        sendTypingStop,
        socket, // Expose socket for useChat hook
        isAuth, // Expose auth state
        user, // Expose user data
    };

    return (
        <ChatContext.Provider value={chatContextValue}>
            {children}
        </ChatContext.Provider>
    );
};

export default ChatProvider;
